import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { cx } from '../../cx'
import { X } from '../../icons'
import styles from './Toast.module.css'

export type ToastTone = 'info' | 'success' | 'danger'

export interface ToastOptions {
  tone?: ToastTone
  /** Czas auto-zamknięcia w ms; 0 wyłącza auto-dismiss. */
  duration?: number
}

export interface ToastContextValue {
  show: (message: ReactNode, options?: ToastOptions) => number
  dismiss: (id: number) => void
}

interface ToastItem {
  id: number
  message: ReactNode
  tone: ToastTone
}

const ToastContext = createContext<ToastContextValue | null>(null)

export const TOAST_DEFAULT_DURATION = 4000

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext)
  if (context === null) {
    throw new Error('useToast: wymaga zamontowanego <ToastProvider>')
  }
  return context
}

export function ToastProvider({ children }: { children?: ReactNode }) {
  const [toasts, setToasts] = useState<readonly ToastItem[]>([])
  const nextId = useRef(1)
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>())

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id)
    if (timer !== undefined) {
      clearTimeout(timer)
      timers.current.delete(id)
    }
    setToasts((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const show = useCallback(
    (message: ReactNode, options?: ToastOptions): number => {
      const id = nextId.current
      nextId.current += 1
      const tone = options?.tone ?? 'info'
      const duration = options?.duration ?? TOAST_DEFAULT_DURATION
      setToasts((current) => [...current, { id, message, tone }])
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => {
            dismiss(id)
          }, duration),
        )
      }
      return id
    },
    [dismiss],
  )

  useEffect(() => {
    const map = timers.current
    return () => {
      for (const timer of map.values()) clearTimeout(timer)
      map.clear()
    }
  }, [])

  const value = useMemo<ToastContextValue>(() => ({ show, dismiss }), [show, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.viewport}>
        {toasts.map((toast) => (
          <Toast key={toast.id} tone={toast.tone} onDismiss={() => dismiss(toast.id)}>
            {toast.message}
          </Toast>
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export interface ToastProps {
  tone?: ToastTone
  onDismiss?: () => void
  children?: ReactNode
  className?: string
}

export function Toast({ tone = 'info', onDismiss, children, className }: ToastProps) {
  return (
    <div role="status" className={cx(styles.toast, styles[tone], className)}>
      <span className={styles.message}>{children}</span>
      {onDismiss !== undefined ? (
        <button type="button" className={styles.close} aria-label="Zamknij" onClick={onDismiss}>
          <X size={14} />
        </button>
      ) : null}
    </div>
  )
}
